"use client";

import { Rnd } from "react-rnd";
import type { RndResizeCallback } from "react-rnd";
import { useCallback } from "react";
import { useWindowStore } from "./windowStore";
import type { WindowRecord } from "./windowTypes";

export default function DraggableWindow({
  win,
  children,
}: {
  win: WindowRecord;
  children: React.ReactNode;
}) {
  const updateBounds = useWindowStore((s) => s.updateBounds);
  const closeWindow = useWindowStore((s) => s.closeWindow);
  const focusWindow = useWindowStore((s) => s.focusWindow);

  const onDragStop = useCallback(
    (_e: unknown, d: { x: number; y: number }) => {
      updateBounds(win.id, { x: d.x, y: d.y });
    },
    [win.id, updateBounds]
  );
  const onResizeStop = useCallback<RndResizeCallback>(
    (_e, _dir, ref, _delta, position) => {
      updateBounds(win.id, {
        width: parseInt((ref as HTMLDivElement).style.width, 10),
        height: parseInt((ref as HTMLDivElement).style.height, 10),
        x: position.x,
        y: position.y,
      });
    },
    [win.id, updateBounds]
  );

  const isResizable = win.isResizable !== false;
  const isFrameless = win.isFrameless === true;
  const contentStyle: React.CSSProperties | undefined = isFrameless
    ? {
        transform: `rotate(${
          win.rotationDeg ?? 0
        }deg) translateZ(0)` as unknown as string,
        transformOrigin: "center",
      }
    : undefined;

  return (
    <Rnd
      style={{ zIndex: win.zIndex }}
      size={{ width: win.bounds.width, height: win.bounds.height }}
      position={{ x: win.bounds.x, y: win.bounds.y }}
      minWidth={win.minWidth}
      minHeight={win.minHeight}
      bounds="window"
      cancel=".no-drag"
      enableResizing={
        isResizable
          ? {
              top: true,
              right: true,
              bottom: true,
              left: true,
              topLeft: true,
              topRight: true,
              bottomLeft: true,
              bottomRight: true,
            }
          : false
      }
      disableDragging={false}
      onDragStart={() => focusWindow(win.id)}
      onDragStop={onDragStop}
      onResizeStart={() => focusWindow(win.id)}
      onResizeStop={onResizeStop}
    >
      {isFrameless ? (
        <div className="relative h-full w-full group" style={contentStyle}>
          {children}
          <button
            type="button"
            className="no-drag absolute top-1 right-1 h-7 w-7 rounded-md bg-white/70 dark:bg-black/40 border border-[--color-border] shadow flex items-center justify-center text-foreground hover:bg-white/90 dark:hover:bg-black/60 transition-opacity opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--color-border] z-20 pointer-events-auto"
            aria-label="Close"
            title="Close"
            onClick={() => closeWindow(win.id)}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-[--color-border] bg-background shadow-xl h-full w-full flex flex-col overflow-hidden">
          <div className="h-10 px-3 flex items-center justify-between border-b border-[--color-border] cursor-move select-none">
            <div className="font-medium text-sm">{win.title}</div>
            <button
              className="h-8 px-3 rounded-md bg-[--surface] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--color-border]"
              onClick={() => closeWindow(win.id)}
              aria-label="Close window"
            >
              Close
            </button>
          </div>
          <div className="flex-1 p-3 overflow-auto">{children}</div>
        </div>
      )}
    </Rnd>
  );
}
